import { useQueryState } from 'nuqs'
import { Suspense } from 'react'
import { useParams } from 'react-router'
import { AGENT_RUN_PARAM, useDocumentAgentRuns } from '~/entities/document-agent'
import { getApiErrorStatus } from '~/shared/lib/api-error'
import { Alert, Button, SkeletonBlock, Text } from '~/shared/ui'
import { DocumentPaneProvider } from '../model'
import { AgentRunHeader } from './agent-run-header'
import { RunDetail, RunDetailSkeleton } from './run-detail'

export function AgentRunPage() {
  const documentKey = useParams<'documentKey'>().documentKey as string
  const [runParam, setRunParam] = useQueryState(AGENT_RUN_PARAM)
  const { data, error, isPending, isError, refetch } = useDocumentAgentRuns(documentKey)

  if (isPending) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <SkeletonBlock className="h-12 w-[420px]" />
        <RunDetailSkeleton />
      </div>
    )
  }

  if (isError) {
    const status = getApiErrorStatus(error)
    return (
      <div className="flex flex-col items-start gap-3 p-6">
        <Alert
          className="grow-0"
          appearance="ERROR"
          materialIcon="error"
        >
          {status === 404
            ? `Document ${documentKey} was not found.`
            : status === 403
              ? 'Agent runs are not available for your account.'
              : 'Could not load the agent runs for this document.'}
        </Alert>
        <div className="flex gap-2">
          {status !== 404 && status !== 403 && <Button onClick={() => refetch()}>Try again</Button>}
          <Button to="/agent-runs">Back to agent runs</Button>
        </div>
      </div>
    )
  }

  const { document, runs } = data
  const selectedRunId = runParam ?? runs[0]?.run_id
  const selectRun = (runId: string) => setRunParam(runId)

  return (
    // Keyed by run, so a quote or page asked for in one run is not replayed in the next.
    <DocumentPaneProvider key={selectedRunId}>
      <div className="flex h-full flex-col overflow-hidden">
        <AgentRunHeader
          document={document}
          runs={runs}
          selectedRunId={selectedRunId}
          onSelectRun={selectRun}
        />
        {runs.length === 0 || !selectedRunId ? (
          <Text
            className="text-text-secondary p-6"
            appearance="BODY"
          >
            This document has no agent runs.
          </Text>
        ) : (
          <Suspense
            fallback={
              <div className="bg-surface-low flex-1 p-6">
                <div className="mx-auto max-w-[960px]">
                  <RunDetailSkeleton />
                </div>
              </div>
            }
          >
            <RunDetail
              key={selectedRunId}
              documentKey={documentKey}
              runId={selectedRunId}
              runs={runs}
              onSelectRun={selectRun}
            />
          </Suspense>
        )}
      </div>
    </DocumentPaneProvider>
  )
}
